import {
  addDays,
  addMinutes,
  format,
  isAfter,
  isToday,
  startOfDay,
} from 'date-fns';
import i18n, { t } from 'i18next';
import { isEqual, isNil } from 'lodash';
import { SalesOrderReceipt } from 'screens/permit/SalesOrderReceiptClass';
import { useAppConfigStore } from 'stores/appConfigStore';
import { usePermitStore } from 'stores/permitStore';
import { Subscription } from 'stores/subscriptionStore';
import { ParkzonePmcUnion, ResolvedPermit } from 'types/common';
import {
  AccessPermitPoolingGroupCombinedInfo,
  BillingFrequency,
  BillingInterval,
  BillingType,
  CompanyBenefit,
  LicensePlate,
  Maybe,
  OrderLine,
  ParkingSpace,
  ParkZoneBenefit,
  Permit,
  Pmc,
  PmcVisibility,
  PoolParking,
  PredefinedTicket,
  SalesOrderStatus,
  UnifiedPermit,
  Vehicle,
} from 'types/generatedSchemaTypes';
import {
  countryStartOfDayToUtc,
  getRemainingDays,
  parseISO8601Duration,
} from 'utils/timeUtils';
import { getLocale, Localized } from './commonUtils';
import { useUiStore } from 'stores/uiStore';

type PermitNavigationRoute =
  | 'multiPoolingPermit'
  | 'permit'
  | 'salesOrderPermitEdit';

const determineNavigationConfig = (
  permit: UnifiedPermit
): { route: PermitNavigationRoute; params: any } => {
  if (isItemAccessPermit(permit)) {
    return handleAccessPermitNavigation(permit);
  }

  if (isSalesOrderRequiringEdit(permit)) {
    return handleSalesOrderEditNavigation(permit);
  }

  const { id: permitId } = permit;

  return {
    route: 'permit',
    params: { permitId },
  };
};

const isSalesOrderRequiringEdit = (permit: UnifiedPermit): boolean => {
  if (permit.permitType !== 'SALES_ORDER') {
    return false;
  }

  return (
    permit.paymentState === 'FAILED_PAY' ||
    isFailedRecurringPayment(permit) ||
    permit.orderStatus === 'WAITING_FOR_ACTIVATION' ||
    permit.orderStatus === 'WAITING_FOR_PAYMENT' ||
    isPermitMissingVehicle(permit?.registrationPlates)
  );
};

const handleAccessPermitNavigation = (
  permit: UnifiedPermit
): { route: PermitNavigationRoute; params: any } => {
  const hasMultiplePoolingGroups =
    (permit.accessPermitPoolingGroupInfo?.length ?? 0) > 1;
  const { id: permitId } = permit;

  return {
    route: hasMultiplePoolingGroups ? 'multiPoolingPermit' : 'permit',
    params: { permitId },
  };
};

const handleSalesOrderEditNavigation = (
  permit: UnifiedPermit
): { route: PermitNavigationRoute; params: any } => {
  const orderLineIdWithVersion = permit.orderLineIdWithVersion ?? '';

  return {
    route: 'salesOrderPermitEdit',
    params: { orderLineIdWithVersion },
  };
};

export const isFailedRecurringPayment = (permit: UnifiedPermit): boolean => {
  return (
    permit.paymentState === 'FAILED_AUTHORISE' ||
    permit.paymentState === 'FAILED_COMMIT'
  );
};

type RecurringPaymentRetryWindowInput = {
  paymentPeriodStart?: Maybe<string | Date>;
  authoriseFailureMinutes?: Maybe<number>;
  countryCode?: Maybe<string>;
  now?: Date;
};

// Mirrors sales-order-service: once start of the failed payment period (in the
// order's country time zone) + pmc.expirationLimits.authoriseFailure has passed,
// no more payment attempts are made and the order is terminated.
export const isRecurringPaymentRetryWindowExpired = ({
  paymentPeriodStart,
  authoriseFailureMinutes,
  countryCode,
  now = new Date(),
}: RecurringPaymentRetryWindowInput): boolean => {
  if (
    !paymentPeriodStart ||
    !authoriseFailureMinutes ||
    (paymentPeriodStart instanceof Date &&
      Number.isNaN(paymentPeriodStart.getTime()))
  ) {
    return false;
  }
  // validFrom is a LocalDate on the backend, serialized as midnight UTC
  const startDay = (
    paymentPeriodStart instanceof Date
      ? paymentPeriodStart.toISOString()
      : paymentPeriodStart
  ).slice(0, 10);
  const periodStart = countryStartOfDayToUtc(startDay, countryCode);
  if (Number.isNaN(periodStart.getTime())) {
    return false;
  }
  return isAfter(now, addMinutes(periodStart, authoriseFailureMinutes));
};

export const isItemPmc = (item: ParkzonePmcUnion): item is Pmc => {
  return (item as Pmc)?.pmcId !== undefined;
};
export const isItemAccessPermit = (
  item: ParkzonePmcUnion
): item is UnifiedPermit => {
  return (item as UnifiedPermit).permitType === 'ACCESS';
};

export const isItemMgLtpAccessPermit = (
  item: ParkzonePmcUnion
): item is UnifiedPermit => {
  return (item as UnifiedPermit).permitType === 'ACCESS_TO_MEMBER_GROUP_LTP';
};

export const isItemPredefinedTicket = (
  item: ParkzonePmcUnion
): item is PredefinedTicket => {
  return (
    (item as PredefinedTicket)?.uid !== undefined ||
    (item as PredefinedTicket)?.sessionId !== undefined
  );
};

export const isItemSalesOrder = (
  item: ParkzonePmcUnion
): item is UnifiedPermit => {
  return ((item as UnifiedPermit)?.permitType ?? '') === 'SALES_ORDER';
};

export const isWaitingListItem = (
  item: ParkzonePmcUnion
): item is UnifiedPermit => {
  return (item as UnifiedPermit).permitType === 'WAITING_LIST';
};

export const isItemOwned = (item: ParkzonePmcUnion) => {
  if (isItemAccessPermit(item)) {
    return item.endDate;
  }
  if (isItemPmc(item)) {
    return null;
  }
  if (isItemSalesOrder(item)) {
    return isSalesOrderActive(item);
  }
  return item?.endTime || null;
};

export const getPermitsAmount = (
  permits?: Maybe<UnifiedPermit[]>,
  licensePlate?: string
) => {
  return (
    permits?.filter((permit) =>
      permit?.registrationPlates?.find(
        (plate) => plate?.plateText === licensePlate
      )
    ).length || 0
  );
};

export const isActivePermit = (permit: Permit) => {
  if (!permit.expirationDate) {
    return permit?.permitStatus?.status === 'ACTIVE';
  }
  const expirationTime = new Date(permit.expirationDate);
  const isValidDate = !Number.isNaN(expirationTime.getTime());
  if (!isValidDate) {
    return permit?.permitStatus?.status === 'ACTIVE';
  }
  return (
    permit?.permitStatus?.status === 'ACTIVE' ||
    (permit?.permitStatus?.status === 'TERMINATED' &&
      getRemainingDays(expirationTime) >= 0)
  );
};

export const isPermitForParkingZone = (
  parkingZoneUid: string | undefined,
  permit: UnifiedPermit
): boolean => {
  if (permit.permitType === 'ACCESS') {
    return Boolean(
      permit.accessPermitPoolingGroupInfo?.some((group) =>
        group?.parkingZoneUids?.includes(parkingZoneUid ?? '')
      )
    );
  }

  return Boolean(
    parkingZoneUid === permit.parkingZoneUid ||
      permit.salesLocation?.parkingZones?.some(
        (zoneUid) => zoneUid === parkingZoneUid
      )
  );
};

export const isPermitForVehicle = (permit: Permit, selectedVehicle?: Vehicle) =>
  permit?.licensePlates?.[0] === selectedVehicle?.licensePlate?.plateText;

export const durationText = (item: ParkzonePmcUnion) => {
  if (isItemPredefinedTicket(item) && item.duration) {
    const duration = parseISO8601Duration(item.duration);
    if (duration) {
      return duration > 1
        ? `${duration} ${i18n.t('time.days')}`
        : `${duration} ${i18n.t('time.day')}`;
    }
  }
};

/**
 * Checks PMC)is visible based on its visibility and household benefits.
 *
 * @param {Pmc} pmc - The PMC to check for visibility.
 * @param {Maybe<CompanyBenefit>[]} householdBenefits - An array of household benefits.
 * @returns {boolean} `true` if the PMC is visible, `false` otherwise.
 */
export const isPmcVisible = (
  pmc: Pmc,
  householdBenefits: Maybe<CompanyBenefit>[]
): boolean => {
  const hasValidPaymentMethod =
    pmc.paymentMethods?.some((method) => method === 'CUSTOMER_CREDIT_CARD') ??
    false;

  const isPublicOrLocked =
    pmc.visibility === 'PUBLIC' || pmc.visibility === 'LOCKED';

  const isPrivateWithBenefit =
    pmc.visibility === 'PRIVATE' &&
    householdBenefits?.some(
      (benefit) => benefit?.benefitTargetUid === pmc.pmcId
    );

  return hasValidPaymentMethod && (isPublicOrLocked || isPrivateWithBenefit);
};

export const isSalesOrderActive = (
  salesOrder: SalesOrderReceipt | Maybe<UnifiedPermit> | undefined
) =>
  salesOrder?.orderStatus === 'ACTIVE' ||
  salesOrder?.orderStatus === 'ACTIVATING' ||
  salesOrder?.orderStatus === 'UPDATING' ||
  salesOrder?.orderStatus === 'TERMINATING' ||
  salesOrder?.orderStatus === 'TERMINATED';

export const shouldShowSalesOrderDoorCode = ({
  doorCode,
  isHistory = false,
  salesOrder,
}: {
  doorCode?: Maybe<string>;
  isHistory?: boolean;
  salesOrder?: SalesOrderReceipt | Maybe<UnifiedPermit>;
}) => Boolean(doorCode && !isHistory && isSalesOrderActive(salesOrder));

export const isSalesOrderWaiting = (
  salesOrder: SalesOrderReceipt | Maybe<UnifiedPermit> | undefined
) =>
  salesOrder?.orderStatus === 'WAITING_FOR_ACTIVATION' ||
  salesOrder?.orderStatus === 'WAITING_FOR_PAYMENT';

export const isTerminalOrderStatus = (
  orderStatus?: Maybe<SalesOrderStatus>
): boolean =>
  orderStatus === 'CLOSED' ||
  orderStatus === 'EXPIRED' ||
  orderStatus === 'TERMINATED' ||
  orderStatus === 'TERMINATING' ||
  orderStatus === 'TERMINATION_FAILED' ||
  orderStatus === 'FAILED';

export const salesOrderForZone = (
  salesOrder: UnifiedPermit,
  parkZoneUid?: string
) => {
  const orderLine = salesOrder?.orderLines?.[0];
  const orderLineZone = orderLine?.pmc?.physicalZoneUid;

  return orderLineZone === parkZoneUid;
};

export const isSalesOrderForVehicle = (
  orderLine: Maybe<OrderLine> | undefined,
  selectedVehicle?: Vehicle
) =>
  orderLine?.registrationPlates?.[0]?.plateText ===
  selectedVehicle?.licensePlate?.plateText;

export type SalesOrderExpiration = {
  expirationDateString: string;
  isExpired: boolean;
};

export const salesOrderExpiration = (
  receipt: Maybe<SalesOrderReceipt>
): SalesOrderExpiration => {
  if (!receipt) {
    return { isExpired: true, expirationDateString: '' };
  }
  const { lock, created, isWaiting } = receipt || {};
  const dateFormat = (date: Date) =>
    format(new Date(date), isToday(new Date(date)) ? 'HH:mm' : 'dd.MM.yyyy', {
      locale: getLocale(),
    });

  if (lock?.expiration && created) {
    const expirationDate = lock?.expiration;
    return {
      expirationDateString: dateFormat(expirationDate),
      isExpired: isWaiting && isAfter(new Date(), new Date(expirationDate)),
    };
  }
  return { isExpired: false, expirationDateString: '' };
};

/**
 * Sorts an array of PMC items by a specified visibility.
 * @param pmcs - The array of PMC items to sort.
 * @param visibility - The visibility to use for sorting.
 * @returns A new array of PMC items sorted by the specified visibility.
 */
export const sortPmcsByVisibility = (
  pmcs: Maybe<Pmc[]> | undefined,
  visibility: PmcVisibility = 'PRIVATE'
) => {
  return [...(pmcs ?? [])].sort((a, b) => {
    if (a.visibility === visibility && b.visibility !== visibility) {
      return -1;
    } else if (a.visibility !== visibility && b.visibility === visibility) {
      return 1;
    } else {
      return 0;
    }
  });
};

/*
 * Check if access permit is redeemed
 */
export const isAccessPermitRedeemed = (permit: UnifiedPermit): boolean => {
  return !!permit?.registrationPlates && permit?.registrationPlates.length > 0;
};

export const getTicketId = (item: ParkzonePmcUnion): string => {
  switch (item?.__typename) {
    case 'PredefinedTicket':
      return item.uid || '';
    case 'PMC':
      return item.pmcId || '';
    case 'UnifiedPermit':
      return item.id;
    default:
      return '';
  }
};

const generatePaymentInfoText = (
  insidePool: Maybe<PoolParking> | undefined,
  outsidePool: Maybe<PoolParking> | undefined
): string => {
  if (isNil(insidePool) && isNil(outsidePool)) {
    return '';
  }

  if (insidePool === 'USER_PAYS') {
    return i18n.t('permit.access.userPays');
  }
  if (outsidePool === 'USER_PAYS') {
    return i18n.t('permit.access.conditionallyUserPays');
  }
  return i18n.t('permit.access.companyPays');
};

export const getPoolingPaymentInfoText = (permit: UnifiedPermit): string => {
  const poolingInfo = permit?.accessPermitPoolingGroupInfo?.[0];

  return getPoolingGroupPaymentInfoText(poolingInfo);
};

export const getPoolingGroupPaymentInfoText = (
  poolingGroup: Maybe<AccessPermitPoolingGroupCombinedInfo> | undefined
): string => {
  return generatePaymentInfoText(
    poolingGroup?.insidePool,
    poolingGroup?.outsidePool
  );
};

export const formatPermitValid = (
  validDate: Maybe<Date> | undefined,
  includeTime: boolean = false
) => {
  const baseOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  };

  const options: Intl.DateTimeFormatOptions = includeTime
    ? {
        ...baseOptions,
        hour: '2-digit',
        minute: '2-digit',
      }
    : baseOptions;

  return Localized.dateTime(validDate, undefined, options);
};

export const getParkingSpacesText = (
  parkingSpaces: Maybe<ParkingSpace>[] | undefined,
  freetextParkingSpaceNo: Maybe<string> | undefined
): string => {
  if ((parkingSpaces?.length ?? 0) > 3) {
    return `${parkingSpaces?.length} ${i18n.t('permit.spaces')}`;
  }
  if (freetextParkingSpaceNo) {
    return freetextParkingSpaceNo;
  }
  return (
    parkingSpaces
      ?.map((ps) => ps?.name)
      .filter((name): name is string => Boolean(name))
      .sort((a, b) => a.localeCompare(b))
      .join(', ') ?? ''
  );
};

type RegistrationPlateLike = Partial<LicensePlate> | null | undefined;

const normalizeRegistrationPlatesForCompare = (
  plates: ReadonlyArray<RegistrationPlateLike> | null | undefined
): { countryCode: string; plateText: string }[] =>
  (plates ?? [])
    .filter((plate): plate is Partial<LicensePlate> => plate != null)
    .map((plate) => ({
      countryCode: String(plate.countryCode ?? ''),
      plateText: String(plate.plateText ?? ''),
    }))
    .sort((a, b) =>
      `${a.countryCode}\t${a.plateText}`.localeCompare(
        `${b.countryCode}\t${b.plateText}`
      )
    );

export const registrationPlatesContentEqual = (
  a: ReadonlyArray<RegistrationPlateLike> | null | undefined,
  b: ReadonlyArray<RegistrationPlateLike> | null | undefined
): boolean =>
  isEqual(
    normalizeRegistrationPlatesForCompare(a),
    normalizeRegistrationPlatesForCompare(b)
  );

/** Order-line plates first; fall back to permit-level plates (matches account permit edit). */
export const getSalesOrderRegistrationPlatesForEdit = (
  salesOrder: UnifiedPermit | undefined | null,
  orderLine?: OrderLine | null
): LicensePlate[] => {
  const linePlates = orderLine?.registrationPlates ?? [];
  const rootPlates = salesOrder?.registrationPlates ?? [];
  const source = linePlates.length > 0 ? linePlates : rootPlates;

  return source
    .filter((plate): plate is NonNullable<typeof plate> => plate != null)
    .map((plate) => ({
      plateText: plate.plateText,
      countryCode: plate.countryCode,
    }));
};

export const getLicensePlatesText = (licensePlates: LicensePlate[]): string => {
  if (licensePlates?.length === 0) {
    return t('permit.noSelectedVehicle');
  }

  if (licensePlates?.length > 2) {
    return t('permit.access.vehicles_other', {
      count: licensePlates?.length,
    });
  }

  return (
    licensePlates
      ?.map((lp) => lp?.plateText)
      .filter((pt): pt is string => Boolean(pt))
      .join(', ') ?? ''
  );
};

export const doAllPermitsHaveFutureStartDates = (
  salesOrdersForZoneAndVehicle?: UnifiedPermit[]
): boolean => {
  if (!salesOrdersForZoneAndVehicle?.length) {
    return false;
  }
  return salesOrdersForZoneAndVehicle.every((permit) => {
    const startDate = permit.startDate ? new Date(permit.startDate) : null;
    if (!startDate) {
      return false;
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return startDate > today;
  });
};

// Calculate the number of occurrences of each PMC ID in the user's permits
export const compilePmcDetails = (permits: UnifiedPermit[]) => {
  // Reduce to accumulate counts of each pmcId
  const pmcDataMap = permits.reduce(
    (acc, permit) => {
      permit?.orderLines?.forEach((orderLine) => {
        const pmcId = orderLine?.pmcId || '';
        const isWaitingActivation = permit.isWaitingActivation;
        const orderLineIdWithVersion =
          permit.permitType === 'WAITING_LIST'
            ? permit.id
            : (permit?.orderLineIdWithVersion ?? '');
        if (!acc[pmcId]) {
          acc[pmcId] = {
            occurrenceCount: 0,
            isWaitingActivation,
            orderLineIdWithVersion,
          };
        }
        acc[pmcId].occurrenceCount += 1;
      });
      return acc;
    },
    {} as Record<
      string,
      {
        occurrenceCount: number;
        isWaitingActivation: boolean;
        orderLineIdWithVersion: string;
      }
    >
  );

  return Object.entries(pmcDataMap).map(
    ([
      pmcId,
      { occurrenceCount, isWaitingActivation, orderLineIdWithVersion },
    ]) => ({
      pmcId,
      occurrenceCount: occurrenceCount,
      isWaitingActivation,
      orderLineIdWithVersion,
    })
  );
};

export const resolvePermitDetails = (
  permit: Pmc,
  householdBenefits: CompanyBenefit[] | undefined,
  pmcOccurrences: {
    pmcId: string;
    occurrenceCount: number;
    isWaitingActivation: boolean;
    orderLineIdWithVersion: string;
  }[]
) => {
  const occurrenceData = pmcOccurrences.find(
    ({ pmcId }) => pmcId === permit.pmcId
  );
  const occurrenceCount = occurrenceData?.occurrenceCount || 0;
  const isWaitingActivation = occurrenceData?.isWaitingActivation || false;
  const orderLineIdWithVersion = occurrenceData?.orderLineIdWithVersion || '';
  const memberGroup = householdBenefits?.find(
    (benefit) => benefit.benefitTargetUid === permit.pmcId
  );
  const exceedMaximumBenefit = memberGroup?.maxNumberOfActiveBenefits
    ? occurrenceCount >= memberGroup.maxNumberOfActiveBenefits
    : false;
  const isDuplicatePermit = occurrenceCount > 0;
  return {
    memberGroup,
    exceedMaximumBenefit,
    isDuplicatePermit,
    isWaitingActivation,
    occurrenceCount,
    orderLineIdWithVersion,
  };
};

export const showLicensePlate = (item: ParkzonePmcUnion) => {
  if (item?.__typename === 'UnifiedPermit') {
    return item?.usageType === 'LICENSE_PLATE';
  }
  if (item?.__typename === 'PMC') {
    return item?.permitUsageType !== 'CUSTOMER';
  }
  return true;
};

export const isMatchingPoolingPermit = (
  permit: UnifiedPermit,
  parkZoneUid: string
) => {
  return Boolean(
    permit.permitType === 'ACCESS' &&
      (permit.parkingZoneUid === parkZoneUid ||
        permit.salesLocation?.parkingZones?.find(
          (salesLocationParkingZoneUid) =>
            salesLocationParkingZoneUid === parkZoneUid
        ))
  );
};

export const getAccessPermitTitle = (
  permit: UnifiedPermit,
  benefits?: CompanyBenefit[]
): string => {
  const { placeName, parkingFacilityName, accessPermitPoolingGroupInfo } =
    permit;

  // Case 1: Multiple pooling groups
  if (accessPermitPoolingGroupInfo && accessPermitPoolingGroupInfo.length > 1) {
    // Try to get title from benefit name
    const firstPoolingGroup = accessPermitPoolingGroupInfo[0];
    const poolingGroupUid = firstPoolingGroup?.uid;

    if (poolingGroupUid && benefits?.length) {
      const memberGroupName = benefits.find(
        (benefit) => benefit.benefitTargetUid === poolingGroupUid
      )?.name;

      if (memberGroupName) {
        return memberGroupName;
      }
    }

    // Fall back to generic locations text
    return i18n.t('permit.access.locations', {
      count: accessPermitPoolingGroupInfo.length,
    });
  }

  // Case 2: Default to place or facility name
  return placeName || parkingFacilityName || '';
};

const getNonAccessPermitTitle = (permit: UnifiedPermit): string => {
  const { pmcName, orderLines } = permit;
  return getPermitName(permit) || pmcName || orderLines?.[0]?.pmc?.name || '';
};

export const getPermitTitle = (
  permit: UnifiedPermit,
  benefits?: CompanyBenefit[]
): string => {
  const { permitType } = permit;

  const isAccessPermit =
    permitType === 'ACCESS' ||
    permitType === 'ACCESS_TO_MEMBER_GROUP_LTP' ||
    permitType === 'DIGITAL_PERMIT';
  return isAccessPermit
    ? getAccessPermitTitle(permit, benefits)
    : getNonAccessPermitTitle(permit);
};

export const getPermitsForVehicle = (
  permits?: Maybe<UnifiedPermit[]>,
  licensePlate?: string
) => {
  if (!permits?.length || !licensePlate) {
    return [];
  }

  return permits.filter((permit) => {
    const directPlates = permit.registrationPlates ?? [];
    if (directPlates.some((plate) => plate?.plateText === licensePlate)) {
      return true;
    }
    const orderLines = permit.orderLines ?? [];
    return orderLines.some((line) => {
      const linePlates = line?.registrationPlates ?? [];
      return linePlates.some((plate) => plate?.plateText === licensePlate);
    });
  });
};

export const isPermitMissingVehicle = (
  registrationPlates?: Maybe<Array<Maybe<LicensePlate>>>
) => {
  return isNil(registrationPlates) || registrationPlates.length === 0;
};

/**
 * Earliest day an online order can start: today plus the PMC flexible start
 * days, but never before the PMC start selling date.
 */
export const getEarliestPermitStartDate = (
  flexibleStartDays: number,
  startSellingDate?: Maybe<Date | string>
): Date => {
  const flexibleStart = startOfDay(addDays(new Date(), flexibleStartDays));
  if (!startSellingDate) {
    return flexibleStart;
  }
  const startSelling = startOfDay(new Date(startSellingDate));
  return isAfter(startSelling, flexibleStart) ? startSelling : flexibleStart;
};

export const initSalesOrderPmcPurchase = (permit: ParkzonePmcUnion) => {
  const { setSelectedPermitStartDate, setSelectedPermitBillingInterval } =
    usePermitStore.getState();
  if (isItemPmc(permit)) {
    setSelectedPermitStartDate(
      getEarliestPermitStartDate(
        permit.onlineBookingFlexibleStartDate ?? 0,
        permit.startSellingDate
      )
    );
    const autoSelectInterval = permit.billingIntervals?.length === 1;
    const interval = permit.billingIntervals?.find(Boolean);
    setSelectedPermitBillingInterval({
      billingIntervals: permit.billingIntervals ?? [],
      selectedBillingFrequency:
        autoSelectInterval && isIntervalAllowed(interval)
          ? interval
          : undefined,
    });
  } else {
    setSelectedPermitStartDate(startOfDay(new Date()));
    setSelectedPermitBillingInterval(undefined);
  }
};

export const isIntervalAllowed = (
  interval: BillingInterval | BillingFrequency | undefined
): interval is BillingFrequency => {
  if (!interval) {
    return false;
  }
  const availableBillingFrequencies: BillingFrequency[] = [
    'MONTHLY',
    'QUARTERLY',
    'HALF_YEARLY',
    'ANNUALLY',
  ];
  return availableBillingFrequencies.includes(interval as BillingFrequency);
};

export const handlePermitNavigation = (
  permit: UnifiedPermit,
  navigate: (route: PermitNavigationRoute, params: any) => void
): void => {
  const navigationConfig = determineNavigationConfig(permit);
  navigate(navigationConfig.route, navigationConfig.params);
};

export const getPermitName = (
  permit: UnifiedPermit,
  benefits?: CompanyBenefit[]
) => {
  if (permit.permitType === 'ACCESS') {
    return getAccessPermitTitle(permit, benefits);
  }

  const { pmc } = permit.orderLines?.[0] ?? {};
  return (
    permit.placeName ||
    pmc?.parkZone?.name ||
    pmc?.salesLocation?.name ||
    permit.parkingFacilityName ||
    ''
  );
};

export const getAccessPermitPoolingGroupForParkingZone = (
  parkingZoneUid: string | undefined,
  permit: UnifiedPermit | undefined
): Maybe<AccessPermitPoolingGroupCombinedInfo> | undefined => {
  return permit?.accessPermitPoolingGroupInfo?.find((group) =>
    group?.parkingZoneUids?.includes(parkingZoneUid ?? '')
  );
};

export const isPermitWaitingAction = (permit: UnifiedPermit) => {
  if (
    isPermitMissingVehicle(permit?.registrationPlates) &&
    showLicensePlate(permit)
  ) {
    return true;
  }

  if (permit.permitType === 'SALES_ORDER') {
    return permit.isWaitingActivation || isFailedRecurringPayment(permit);
  }

  return false;
};

export const sortPermitsByWaitingAction = (
  permits: UnifiedPermit[] | undefined,
  failedPaymentMap?: Record<string, boolean>
) => {
  if (!permits?.length) {
    return [];
  }

  return [...(permits ?? [])].sort((a, b) => {
    // Check for failed payments first (highest priority)
    const aPaymentFailed = failedPaymentMap?.[a.id] === true;
    const bPaymentFailed = failedPaymentMap?.[b.id] === true;

    if (aPaymentFailed && !bPaymentFailed) {
      return -1;
    }
    if (!aPaymentFailed && bPaymentFailed) {
      return 1;
    }

    // Then check waiting action status
    const aWaiting = isPermitWaitingAction(a);
    const bWaiting = isPermitWaitingAction(b);

    if (aWaiting && !bWaiting) {
      return -1;
    }
    if (!aWaiting && bWaiting) {
      return 1;
    }

    return 0;
  });
};

export const checkPrepaidOrPermitTicketDates = (
  purchasedPrepaidTickets: any[],
  selectedParkzonePmcUnion: any,
  vehicle: any,
  permitStartDate?: Date,
  selectedSegment?: string
): Date => {
  if (
    purchasedPrepaidTickets.length > 0 &&
    isItemPredefinedTicket(selectedParkzonePmcUnion)
  ) {
    const ticketsByLicensePlate = purchasedPrepaidTickets.filter(
      (ticket: PredefinedTicket) =>
        ticket?.accessDevice?.value === vehicle?.licensePlate?.plateText &&
        selectedSegment === ticket?.customerSegmentUid
    );
    if (ticketsByLicensePlate.length < 1) {
      return new Date();
    }
    const maxDate = ticketsByLicensePlate.reduce((a, b) => {
      if (a?.endTime && b?.endTime) {
        return new Date(a.endTime) > new Date(b.endTime) ? a : b;
      }
      return a;
    }, ticketsByLicensePlate[0]);

    const maxEndDate = maxDate?.endTime
      ? new Date(maxDate.endTime)
      : new Date();

    return maxEndDate;
  } else if (isItemPmc(selectedParkzonePmcUnion) && permitStartDate) {
    return permitStartDate;
  }
  return new Date();
};

export const handlePermitConditions = (
  permit: ResolvedPermit,
  pmc: Pmc,
  navigationType: string,
  pmcIsLockedForTheCustomer: boolean,
  pmcIsInsideALockedMemberGroup: boolean
): boolean => {
  const { isWaitingActivation, exceedMaximumBenefit, isDuplicatePermit } =
    permit;

  // Handle permit conditions in order of importance
  if (isWaitingActivation) {
    useUiStore.getState().setSheetModal('permitActivationNeeded');
    return true;
  }
  if (exceedMaximumBenefit) {
    useUiStore.getState().setSheetModal('limitReach');
    return true;
  }
  if (isDuplicatePermit) {
    usePermitStore.getState().setSelectedPmc(pmc);
    useAppConfigStore.getState().setNavigateType(navigationType);
    useUiStore.getState().setSheetModal('duplicatePermitWarning');
    return true;
  }

  if (
    isItemPmc(permit) &&
    pmcIsLockedForTheCustomer &&
    pmcIsInsideALockedMemberGroup
  ) {
    usePermitStore.getState().setSelectedPmc(pmc);
    useAppConfigStore.getState().setNavigateType(navigationType);
    useUiStore.getState().setSheetModal('unlockMemberGroupPmc');
    return true;
  }

  if (
    isItemPmc(permit) &&
    pmcIsLockedForTheCustomer &&
    !pmcIsInsideALockedMemberGroup
  ) {
    useAppConfigStore.getState().setNavigateType(navigationType);
    useUiStore.getState().setSheetModal('unlockPmc');
    return true;
  }

  if (isPmcAvailable(permit).permitNotAvailable) {
    return true;
  }

  return false;
};

export const isSalesOrderRenewable = (
  billingType: Maybe<BillingType> | undefined,
  renewal: boolean | null | undefined
) => {
  return !!(
    renewal &&
    (billingType === 'CALENDAR' ||
      billingType === 'ANNIVERSARY' ||
      billingType === 'CALENDAR_FULL')
  );
};

export const getBillingType = (
  billingTypes?: Maybe<Maybe<BillingType>[]> | undefined
): BillingType | undefined => {
  if (!billingTypes || billingTypes.length === 0) {
    return undefined;
  }

  const filteredBillingTypes = billingTypes.filter(
    (billingType): billingType is BillingType => !!billingType
  );

  if (filteredBillingTypes.includes('ANNIVERSARY')) {
    return 'ANNIVERSARY';
  }

  if (filteredBillingTypes.includes('CALENDAR')) {
    return 'CALENDAR';
  }

  // Maybe 'CALENDAR_FULL' in the return value
  return filteredBillingTypes[0];
};

export const isPmcAvailable = (pmc: Maybe<Pmc> | undefined) => {
  const amountAvailable = pmc?.availability?.availability ?? 0;
  const availabilityZero = amountAvailable === 0;
  const waitingListIsEnabled = !!pmc?.enableWaitingList;
  const showWaitingList = availabilityZero && waitingListIsEnabled;
  const permitNotAvailable = availabilityZero && !waitingListIsEnabled;
  return {
    availabilityZero,
    waitingListIsEnabled,
    showWaitingList,
    permitNotAvailable,
    amountAvailable,
  };
};

// Helper function to extract common values from either subscription type
type SubscriptionData = Pmc | SalesOrderReceipt;

export const getSubscriptionData = (
  subscription: SubscriptionData | null
): Subscription | undefined => {
  if (!subscription) {
    return undefined;
  }
  // Check if it's a SalesOrderReceipt (customer subscription)
  if (subscription instanceof SalesOrderReceipt) {
    const orderline = subscription?.item?.orderLines?.[0];
    return {
      uid: subscription?.uid,
      billingInterval: orderline?.billingFrequency as BillingInterval,
      validFrom: subscription?.created ?? undefined,
      name: orderline?.pmc?.name ?? undefined,
      description: orderline?.pmc?.description ?? undefined,
      countryCode: subscription.countryCode ?? undefined,
      prices: orderline?.pmc?.prices,
      renewal: subscription.renewal,
      creditCardId: subscription.creditCardId,
      status: subscription.orderStatus ?? undefined,
      nextBillingDate: subscription.nextBillingDate,
      orderLines: subscription.item?.orderLines ?? undefined,
      orderLineIdWithVersion:
        subscription.item?.orderLineIdWithVersion ?? undefined,
      endDate: subscription.item?.endDate ?? undefined,
    };
  }
  // Otherwise it's a Pmc (available subscription)
  return {
    billingInterval: subscription?.billingInterval as BillingInterval,
    validFrom: new Date(),
    name: subscription?.name ?? undefined,
    description: subscription?.description ?? undefined,
    countryCode: subscription?.countryCode,
    prices: subscription?.prices,
    isFirstMonthFree: subscription?.isFirstMonthFree ?? undefined,
  };
};

/**
 * @param pmcIsLockedForTheCustomer - Whether the PMC is locked for the customer at the moment
 * @param visibility - The original visibility state of the PMC
 * @param pmcIsInsideALockedMemberGroup - Whether the PMC carries a member-group unlock code at all
 * @returns The effective visibility state: 'LOCKED' while the member group lock
 * applies, 'PUBLIC' once the member group has granted access
 */
export const getEffectiveVisibility = (
  pmcIsLockedForTheCustomer: boolean | undefined,
  visibility: Maybe<PmcVisibility> | undefined,
  pmcIsInsideALockedMemberGroup?: boolean
): Maybe<PmcVisibility> | undefined => {
  // If the PMC is locked for the customer, it's always 'LOCKED''
  if (pmcIsLockedForTheCustomer) {
    return 'LOCKED';
  }

  // Not locked for the customer but inside a locked member group:
  // this only happens when the customer has already MG unlock
  // code, so the MG unlock over any raw 'LOCKED' visibility...
  if (pmcIsInsideALockedMemberGroup) {
    return 'PUBLIC';
  }
  return visibility;
};

/**
 * Resolves the member-group unlock-code lock state for a PMC.
 *
 * @param pmcId - The PMC to resolve the lock state for.
 * @param parkingZoneBenefits - Benefits available on the parking zone.
 * @param householdBenefits - The customer's household benefits.
 * @param visibility - The PMC's own visibility. A PUBLIC pmc is never locked,
 * even if it happens to carry an unlock-code benefit for some other purpose.
 */
export const resolvePmcMemberGroupLock = (
  pmcId: Maybe<string> | undefined,
  parkingZoneBenefits: ParkZoneBenefit[],
  householdBenefits: Maybe<CompanyBenefit[]> | undefined,
  visibility?: Maybe<PmcVisibility>
): {
  memberGroupBenefit: ParkZoneBenefit | undefined;
  pmcIsInsideALockedMemberGroup: boolean;
  pmcIsLockedForTheCustomer: boolean;
} => {
  const memberGroupBenefit =
    visibility === 'PUBLIC'
      ? undefined
      : parkingZoneBenefits.find(
          (b) =>
            b.admissionTypes?.includes('UNLOCK_CODE_MATCH') &&
            b.benefitTargetUid === pmcId
        );

  const pmcIsInsideALockedMemberGroup = !!memberGroupBenefit;

  const customerHasActiveBenefit = householdBenefits?.some(
    (benefit) => benefit?.benefitTargetUid === pmcId
  );

  const pmcIsLockedForTheCustomer =
    (pmcIsInsideALockedMemberGroup && !customerHasActiveBenefit) ||
    (!pmcIsInsideALockedMemberGroup && visibility === 'LOCKED');

  return {
    memberGroupBenefit,
    pmcIsInsideALockedMemberGroup: pmcIsInsideALockedMemberGroup,
    pmcIsLockedForTheCustomer: pmcIsLockedForTheCustomer,
  };
};
